import * as React from 'react';
import { useSelector } from 'react-redux';
import { CardLabelList } from '../../components/CardLabelList';
import { GkCardLayout } from '../../components/GKCardLayout';
import { GKCardButtonsLayout } from '../../components/GKCardButtonsLayout';
import { getIsNewbuildingHiddenByIdSelector, getNewbuildingByIdSelector } from '../../selectors/newbuilding';
import { getNewbuildingIsUpcomingSale } from '../../selectors/getNewbuildingIsUpcomingSale';
import { getNewbuildingUrl } from '../../selectors/getNewbuildingUrl';
import { isNewbuildingInLeadFactory } from '../../utils/helpers';
import { ReviewLinkContainer } from '../ReviewLinkContainer';
import { GeoDistance } from '../../components/GeoDistance';
import { AddressLine } from '../../components/AddressLine';
import { Advertises } from './Advertise';
import { UpcomingSaleButtonContainer } from '../UpcomingSaleButtonContaier';
import { MessageButtonContainer } from '../MessageButtonContainer';
import { FavoriteButton } from './FavoriteButton';
import { RoomCounts } from './RoomCounts';
import { CianInfo } from '../../components/CianInfo';
import { BuilderInfo } from './BuilderInfo';
import { IApplicationState } from '../../types/redux';
import { CarouselContainer } from './Carousel';
import { NewbuildingInfoContainer } from './NewbuildingInfo';
import { MetroInfo } from '../../components/MetroInfo';
import { GeoBlock } from '../../components/GeoBlock';
import { Description } from '../../components/Description';
import { CardInfoLayout } from '../../components/CardInfoLayout';
import { ContactsBlock } from '../../components/ContactsBlock';
import { PhoneButton } from '../ShowPhoneButton';
import { CardHeaderLayout } from '../../components/CardHeaderLayout';

interface IGKCardProps {
  newbuildingId: number;
  onCardClick(offerId: number): void;
}

export const GKCard = (props: IGKCardProps) => {
  const { newbuildingId, onCardClick } = props;
  const urlFinisher = useSelector((state: IApplicationState) => state.location.path);
  const newbuilding = useSelector(getNewbuildingByIdSelector(newbuildingId));
  const isNewbuildingHidden = useSelector(getIsNewbuildingHiddenByIdSelector(newbuildingId));

  const [hovered, setHovered] = React.useState(false);

  const handleCardClick = React.useCallback(() => {
    onCardClick(newbuildingId);
  }, [newbuildingId, onCardClick]);

  /* istanbul ignore next */
  if (!newbuilding || isNewbuildingHidden) {
    return null;
  }

  const newbuildingUrl = getNewbuildingUrl(newbuilding);
  const isUpcomingSale = getNewbuildingIsUpcomingSale(newbuilding);
  const isLeadFactory = isNewbuildingInLeadFactory(newbuilding);

  return (
    <GkCardLayout onHover={setHovered}>
      <CarouselContainer newbuildingId={newbuildingId} isLeadFactory={isLeadFactory} />
      <CardHeaderLayout>
        <NewbuildingInfoContainer onCardClick={handleCardClick} newbuildingId={newbuildingId} hovered={hovered} />
        <ReviewLinkContainer newbuildingId={newbuilding.id} newbuildingUrl={newbuildingUrl} newbuilding={newbuilding} />
        <CardLabelList labels={newbuilding.labels} />
        <CardInfoLayout>
          <GeoBlock>
            <MetroInfo undergroundInfo={newbuilding.undergroundInfo} regionId={newbuilding.regionId} />
            <GeoDistance seoGeoDistance={newbuilding.seoGeoDistance} urlFinisher={urlFinisher} />
            <AddressLine addressLine={newbuilding.addressLine} />
          </GeoBlock>

          {!isUpcomingSale && (
            <Advertises
              fromDevelopersRooms={newbuilding.fromDevelopersRooms}
              fromAgentsCountsDisplay={newbuilding.fromAgentsCountsDisplay}
              fromAgentsPropsCount={newbuilding.fromAgentsPropsCount}
              fromAgentsPropsLink={newbuilding.fromAgentsPropsLink}
            />
          )}

          <Description description={newbuilding.description} />
        </CardInfoLayout>
      </CardHeaderLayout>
      <ContactsBlock
        favorite={
          <FavoriteButton
            isFavorite={Boolean(newbuilding.isFavorite)}
            newbuildingId={newbuilding.id}
            gaLabelFull={newbuilding.gaLabels.full}
          />
        }
        contact={
          isLeadFactory ? <CianInfo sellers={newbuilding.sellers} /> : <BuilderInfo builders={newbuilding.builders} />
        }
        controls={
          <GKCardButtonsLayout>
            {isUpcomingSale ? (
              <UpcomingSaleButtonContainer newbuildingId={newbuilding.id} gaLabel={newbuilding.gaLabels.full} />
            ) : (
              <PhoneButton newbuildingId={newbuilding.id} />
            )}
            <MessageButtonContainer newbuilding={newbuilding} />
          </GKCardButtonsLayout>
        }
        link={
          <RoomCounts
            fromDeveloperPropsLink={newbuilding.fromDeveloperPropsLink}
            fromDeveloperPropsCount={newbuilding.fromDeveloperPropsCount}
            fromDeveloperCountsDisplay={newbuilding.fromDeveloperCountsDisplay}
          />
        }
      />
    </GkCardLayout>
  );
};
